import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Handshake from "@mui/icons-material/Handshake";
import { useTranslation } from 'react-i18next';
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState } from "react";
import { CLIENTS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import ClientForm from "../../components/clientForm/ClientForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useHasPermission } from "../../contexts/auth";
import ContractMultistepForm from "../../components/ui/contractMultistepForm/contractMultistepForm.component";

export const ClientsDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(CLIENTS_ENDPOINT);
  const [updateClientOpened, setUpdateClientOpened] = useState<boolean>(false);
  const [deleteClientOpened, setDeleteClientOpened] = useState<boolean>(false);
  const [addContractOpened, setAddContractOpened] = useState<boolean>(false);
  const canChangeClient = useHasPermission("settlement.change_client");
  const canDeleteClient = useHasPermission("settlement.delete_client");
  const canAddContract = useHasPermission("settlement.add_contract");

  const handleDeleteClient = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Client deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteClientOpened(false);
      navigate('/clients');
    } else {
      enqueueSnackbar(t('Failed to delete client'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <Handshake />,
      label: t("Add new contract"),
      onClick: () => {
        setAddContractOpened(true);
      },
      disabled: !canAddContract
    },
    {
      icon: <EditIcon />,
      label: t("Update client"),
      onClick: () => {
        setUpdateClientOpened(true);
      },
      disabled: !canChangeClient
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete client"),
      onClick: () => {
        setDeleteClientOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteClient
    },
  ];

  const handleContractAdded = () => {
    setAddContractOpened(false);
    window.dispatchEvent(new Event("clientContractAdded"));
    navigate('/clients/' + id + '/contracts');
  };

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this client will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteClient}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateClientOpened}
        setOpened={setUpdateClientOpened}
        title={t("Update client")}
        content={
          <ClientForm
            url={CLIENTS_ENDPOINT}
            id={id}
            onSuccess={() => {
              setUpdateClientOpened(false);
              window.dispatchEvent(new Event("clientUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteClientOpened}
        setOpened={setDeleteClientOpened}
        title={t("Delete client")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={addContractOpened}
        setOpened={setAddContractOpened}
        title={t("Add new contract")}
        content={
          <ContractMultistepForm onSuccess={handleContractAdded} clientId={id} />
        }
      />
    </>
  );
};
